import { Button, Flex } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams, useBlocker } from "react-router-dom"
import { useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { getActionTranslations } from "utils/translations"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import ConnectStripeModal from "layoutsV2/pro/ProNewListingLayout/ConnectPaymentProviderModal"
import { STRIPE_TAB, PAYPAL_TAB, POWERTRANZ_TAB } from "pagesV2/pro/Settings/ProSettings/PaymentProviders/constants"
import { usePowerTranzConnectOffer } from "hooks/usePowerTranzConnectOffer"
import { isPaypalConnectedSelector, isPowerTranzPayableSelector, isStripeFullyConnectedSelector } from "store/user"
import { spacings } from "themes/variables"
import { routes } from "router"
import styles from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons.module.css"
import { useExperienceWizard } from "./useExperienceWizard"
import { useWizardContext } from "./WizardContext"

export const WizardActionButtons = () => {
  const { t } = useTranslation()
  const translations = getActionTranslations(t)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isPublishModalVisible, setIsPublishModalVisible] = useState(false)
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false)

  const { form, isFormDirty, resetFormDirty } = useWizardContext()

  const tripData = useSelector((state) => state?.trips)
  const isStripeConnected = useSelector(isStripeFullyConnectedSelector)
  const isPaypalConnected = useSelector(isPaypalConnectedSelector)
  const isPowerTranzPayable = useSelector(isPowerTranzPayableSelector)
  const shouldShowStripeWarning = !isStripeConnected && !isPaypalConnected && !isPowerTranzPayable

  const routeType = searchParams.get("route") // "published" | "deactivated" | null
  const isEditMode = Boolean(routeType)
  const isReadOnlyPreview = searchParams.get("readOnlyPreview") === "true"

  const { isFirstStep, isLastStep, isSaving, handleNext, handlePrevious, handleSaveDraft, handlePublish, navigateToStep } = useExperienceWizard()

  const getBackRoute = () => {
    if (routeType === "deactivated") return routes.proExperiencePathDeactivatedPath()
    if (routeType === "published") return routes.proExperiencePathPublishedPath()
    return routes.proExperiencePathDraft()
  }

  // Block navigation away from the wizard when there are unsaved form changes
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname &&
      isFormDirty &&
      !isSaving
    )
  })

  const isUnsavedModalOpen = blocker.state === "blocked"

  const handleLeave = () => {
    blocker.proceed()
  }

  const handleKeepEditing = () => {
    blocker.reset()
  }

  const handleSaveAndLeave = () => {
    // Reset the blocker first so handleSaveDraft's internal navigate (for new trips) doesn't conflict
    blocker.reset()
    handleSaveDraft(() => {
      resetFormDirty()
      navigate(getBackRoute())
    })
  }

  const onSaveAndExit = () => {
    // A single↔multi-day switch deleted the start times: on a saved listing, Save Update goes on to
    // Schedule (not back to the list) so the guide adds them again for the new duration.
    const isRescheduleNeeded = isEditMode && (form.getFieldValue("cleared_slot_ids") || []).length > 0
    handleSaveDraft(() => {
      resetFormDirty()
      if (isRescheduleNeeded) {
        toaster.info(t("experiences.ticketed_schedule.reschedule_after_duration_change"))
        navigateToStep("scheduling")
        return
      }
      navigate(getBackRoute())
    })
  }

  const onPublish = () => {
    handlePublish(
      () => {
        toaster.success(t("experiences.successful_publish"))
        setIsPublishModalVisible(false)
        resetFormDirty()
        setTimeout(() => navigate(routes.proExperiencesPath()), 300)
      },
      () => setIsPublishModalVisible(false)
    )
  }

  const onPublishClick = () => {
    if (shouldShowStripeWarning) {
      setIsStripeModalOpen(true)
    } else {
      setIsPublishModalVisible(true)
    }
  }

  // Saves the draft, then opens that provider's tab in Settings > Payment Providers.
  const connectProvider = (tab) => () => {
    handleSaveDraft(() => {
      toaster.success(t("experiences.stripe_modal.draft_saved"))
      setIsStripeModalOpen(false)
      resetFormDirty()
      navigate(routes.proSettingsPaymentProviders({}, { tab }))
    })
  }
  const isPowerTranzOffered = usePowerTranzConnectOffer(isStripeModalOpen)

  // The read-only preview edits nothing: its only way out is back to the list it came from.
  if (isReadOnlyPreview) {
    return (
      <div className={styles.wrapper} style={{ flexWrap: "wrap" }}>
        <Flex gap={spacings.S} className="save-wrapper" justify="flex-end">
          <Button className="pr-xl pl-xl" color="primary" variant="solid" size="large" onClick={() => navigate(getBackRoute())}>
            {t("listings.preview_edit")}
          </Button>
        </Flex>
      </div>
    )
  }

  return (
    <div className={styles.wrapper} style={{ flexWrap: "wrap" }}>
      <Flex justify="space-between" gap={10} className={styles.actionWrap}>
        {!isFirstStep && (
          <Button
            className="pr-xl pl-xl previewBtn"
            color="primary"
            variant="outlined"
            size="large"
            onClick={handlePrevious}
            disabled={isSaving}
          >
            {t("global.previous")}
          </Button>
        )}
        {isEditMode && (
          <Button
            className="pr-xl pl-xl previewBtn"
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => navigate(getBackRoute())}
            disabled={isSaving}
          >
            {t("global.cancel")}
          </Button>
        )}
      </Flex>

      <Flex gap={spacings.S} className="save-wrapper" justify="flex-end">
        <Button
          className="pr-xl pl-xl saveExitBtn"
          color="primary"
          variant={isEditMode ? "solid" : "outlined"}
          size="large"
          onClick={onSaveAndExit}
          disabled={isSaving}
        >
          {isEditMode ? t("listings.save_updates") : t("listings.save_draft_and_exit")}
        </Button>

        {isLastStep ? (
          <Button className="pr-xl pl-xl" color="primary" variant="solid" size="large" onClick={onPublishClick} disabled={isSaving}>
            {t("listings.save_publish")}
          </Button>
        ) : (
          <Button className="pr-xl pl-xl nextBtn" color="primary" variant="solid" size="large" onClick={handleNext} disabled={isSaving}>
            {t("listings.save_next")}
          </Button>
        )}
      </Flex>

      <NewConfirmModal
        onClose={() => setIsPublishModalVisible(false)}
        title={translations?.publish?.title}
        message={translations?.publish?.message}
        highlightText={tripData?.trip?.title}
        visible={isPublishModalVisible}
        onConfirm={onPublish}
        confirmLabel="Publish"
        loading={isSaving || tripData?.trip?.loading}
      />

      <ConnectStripeModal
        open={isStripeModalOpen}
        onCancel={() => setIsStripeModalOpen(false)}
        onConnectStripe={connectProvider(STRIPE_TAB)}
        onConnectPaypal={connectProvider(PAYPAL_TAB)}
        onConnectPowerTranz={isPowerTranzOffered ? connectProvider(POWERTRANZ_TAB) : undefined}
      />

      <UnsavedChangesModal
        open={isUnsavedModalOpen}
        onDiscard={handleLeave}
        onKeepEditing={handleKeepEditing}
        onSave={handleSaveAndLeave}
      />
    </div>
  )
}
